import React, { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import vipIconSvg from '@assets/img/icons/vip-icon.svg';
import cn from 'classnames';

import { useBuyBoostX5X10Mutation } from '@/api/queries/boost';
import { ToastNotification } from '@/common/components/ToastNotification/ToastNotification';
import { additionalDiscountForUsdt } from '@/common/config/common';
import { usePaymentStatus } from '@/common/hooks/payment-status';
import { usePurchaseAttempt } from '@/common/hooks/purchase-attempt';
import { useTonAutoConnect } from '@/common/hooks/ton-auto-connect';
import { multiplication } from '@/common/lib/decimal';
import { openTgInvoice } from '@/common/lib/openTgInvoice';
import { SendAnalitics } from '@/common/lib/playdeck';
import { type ActivateVipStatusResponseType } from '@/common/types/query/user';
import {
  H1,
  StringMain,
  StringSmall,
} from '@/components/Typography/Typography';
import { GreyBlurButton } from '@/components/ui/Button/Button';
import { Close } from '@/components/ui/Close/Close';
import { Picture } from '@/components/ui/Picture/Picture';
import TONandStarsBuyButtons from '@/components/ui/TONandStarsBuyButtons/TONandStarsBuyButtons';
import PendingStatus from '@/components/ui/ViewStatusComponents/PendingStatus';
import SuccessfullStatus from '@/components/ui/ViewStatusComponents/SuccessfullStatus';
import { changePaymentStatus } from '@/redux/reducers/paymentsReducer';
import { subtractBalance } from '@/redux/reducers/userReducer';
import { useAppDispatch } from '@/redux/store';

import { ModalContext } from '../Modal/config';

import css from './style.module.scss';

import boostX5Png from '/img/icons/boost-x5.png';
import boostX5Webp from '/img/icons/boost-x5.webp';
import boostX10Png from '/img/icons/boost-x10.png';
import boostX10Webp from '/img/icons/boost-x10.webp';
import boostSvg from '/img/icons/BoostX5X10/boostRight.svg';
import greylightPng from '/img/icons/BoostX5X10/light.png';
import greylightWebp from '/img/icons/BoostX5X10/light.webp';

interface prop {
  plantId: string;
  minutes: number;
  price: number;
  starPrice: number;
}

const BuyBoostX5X10: React.FC<prop> = ({
  plantId,
  price,
  starPrice,
  minutes,
}) => {
  const { t } = useTranslation();
  const { thirdModal, secondModal } = useContext(ModalContext);
  const [onBuyBoost, { isLoading }] = useBuyBoostX5X10Mutation();
  const connectWalletAndDoTransaction = useTonAutoConnect();
  const paymentStatus = usePaymentStatus();
  const dispatch = useAppDispatch();
  const usdPrice = useMemo(
    () => multiplication(price, additionalDiscountForUsdt.coefficient),
    [price],
  );

  usePurchaseAttempt({
    item: minutes === 5 ? 'boost_x5' : 'boost_x10',
    item_id: plantId,
    price,
    currency: 'usdt',
  });

  const sendAnalitics = (currency: 'ton' | 'xtr' | 'usdt'): void => {
    SendAnalitics.purchaseSuccessfull({
      item: minutes === 5 ? 'boost_x5' : 'boost_x10',
      item_id: plantId,
      price,
      currency,
    });
  };

  const onClick = async (currency: 'XTR' | 'TON'): Promise<void> => {
    try {
      if (currency === 'TON') {
        connectWalletAndDoTransaction(
          async () =>
            (await onBuyBoost({
              plantId,
              minutes,
              currency,
            }).unwrap()) as ActivateVipStatusResponseType,
          {
            onSuccess: () => {
              dispatch(changePaymentStatus('pending'));
              ToastNotification(t('Payment will be credited soon.'));
              sendAnalitics('ton');
            },
            onError: () => {
              dispatch(changePaymentStatus(undefined));
              ToastNotification(t('Something went wrong, please try again'));
            },
          },
        );
      } else {
        const response = await onBuyBoost({
          plantId,
          minutes,
          currency,
        }).unwrap();

        if (response && 'link' in response) {
          openTgInvoice(response.link, {
            errorMessage: { image: { img: vipIconSvg } },
            onSuccess: () => {
              dispatch(changePaymentStatus('pending'));
              sendAnalitics('xtr');
            },
            playdeckRequestPayload: {
              amount: starPrice,
              description: 'buy boosts x5 x10',
              externalId: `buy-boosts-x5-x10-${starPrice}`,
            },
          });
        }
      }
    } catch (e) {
      console.error(e);
      dispatch(changePaymentStatus(undefined));
    }
  };

  const buyUsdt = async (): Promise<void> => {
    try {
      await onBuyBoost({
        plantId,
        minutes,
        currency: 'USDT',
      }).unwrap();

      if (thirdModal.isOpen) {
        thirdModal.closeModal();
      } else {
        secondModal.closeModal();
      }
      dispatch(subtractBalance({ type: 'usdt', value: usdPrice }));
      sendAnalitics('usdt');
    } catch (error) {
      console.error(error);
      ToastNotification(t('Something went wrong, please try again'));
      dispatch(changePaymentStatus(undefined));
    }
  };

  const handleClose = (): void => {
    secondModal.closeModal();
    dispatch(changePaymentStatus(undefined));
  };

  const isX5Boost = minutes === 5;

  return (
    <div className="flex flex-col items-center pb-[3rem] pt-[calc(var(--header-height)_+_var(--tg-safe-area-inset-top)_+_2.5rem)] h-full">
      {paymentStatus === 'pending' ? (
        <PendingStatus />
      ) : paymentStatus === 'success' ? (
        <div className="flex flex-col flex-1 gap-[32px] items-center pt-[4rem]">
          <SuccessfullStatus>
            <div className="bg-white/10 rounded-[8px] relative p-[13px] text-center flex flex-col opacity-[0.6]">
              <StringSmall
                weight="bold"
                className={cn(
                  'italic font-bold text-2xl leading-[100%] tracking-0 text-center items-end !text-[24px]',
                  minutes === 5 ? css.x5Shadow : css.x10Shadow,
                )}
              >
                x{minutes}
              </StringSmall>
              <img src={boostSvg} alt="resource" width={136} />
              <StringSmall weight="bold" className="!text-[24px]">
                {minutes} min
              </StringSmall>
            </div>
          </SuccessfullStatus>
        </div>
      ) : (
        <>
          <H1>{t('Go Full Speed')}</H1>
          <div className="flex flex-col items-center flex-1 justify-center">
            <div className="flex relative mb-[26px]">
              <Picture
                img={greylightPng}
                webp={greylightWebp}
                className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 -z-[1] w-[428px]"
                alt="light"
              />
              <Picture
                className="w-[147px]"
                img={isX5Boost ? boostX5Png : boostX10Png}
                webp={isX5Boost ? boostX5Webp : boostX10Webp}
                alt={isX5Boost ? 'boost x5' : 'boost x10'}
              />
            </div>
            <span className={cn(css.mult, isX5Boost ? css.x5 : css.x10)}>
              {isX5Boost ? 'x5' : 'x10'}
            </span>
            <div className={css.time}>{t('10 min.')}</div>
          </div>
          <StringMain weight="extrabold" className="!text-[24px] mb-[16px]">
            $ {price}
          </StringMain>
          <div className="flex flex-col self-stretch px-6">
            <TONandStarsBuyButtons
              onClick={onClick}
              isLoading={isLoading}
              price={price}
              usdPrice={usdPrice}
              discount={additionalDiscountForUsdt.percentage}
              starPrice={starPrice}
              mustBeUsdtButton={true}
              onUsdtBuy={buyUsdt}
              variant="switch"
              classNames="!px-0"
            />
            <GreyBlurButton
              className="mt-3"
              onClick={handleClose}
              text={t('Close')}
            />
          </div>
        </>
      )}
      {paymentStatus === 'pending' && (
        <Close
          onClick={handleClose}
          imageVariant="modal"
          size="lg"
          className="absolute bottom-[calc(var(--tg-safe-area-inset-bottom,0px)+30px)]"
        />
      )}
    </div>
  );
};

export default BuyBoostX5X10;
